/**
 * Permission Storage Class
 * 用于存储和管理路由权限信息
 * - key: 路由名称 (currentRoute.name)
 * - value: 权限值集合 (自动去重)
 */

const whitePermissionValueList = [
  'PC.Dashboard.RegionOverview',
  'PC.SupportPanel',
  'PC.ReportCenter.ReportCenter',
  'PC.Settings.JumpWFM',
];

/**
 * 将路由名称转换为权限值格式
 * @param routeName - 路由名称，例如: 'salesOutbound.wavePlan'
 * @returns 权限值格式，例如: 'PC.SalesOutbound.WavePlan'
 * @example
 * convertRouteNameToPermission('salesOutbound.wavePlan') // 'PC.SalesOutbound.WavePlan'
 * convertRouteNameToPermission('salesOutbound') // 'PC.SalesOutbound'
 */
export const convertRouteNameToPermission = (
  routeName: string | symbol | null | undefined
): string => {
  if (!routeName) return '';

  const routeNameStr = String(routeName);
  if (!routeNameStr) return '';

  // 按点分割路由名称
  const parts = routeNameStr.split('.');

  // 将每个部分首字母大写，其余保持原样
  const formattedParts = parts.map((part) => {
    if (!part) return '';
    return part.charAt(0).toUpperCase() + part.slice(1);
  });

  // 拼接为权限值格式
  return `PC.${formattedParts.join('.')}`;
};

class PermissionStorage {
  private static instance: PermissionStorage;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  private storage: Map<string, Set<string>> = new Map();

  public static getInstance(): PermissionStorage {
    if (!PermissionStorage.instance) {
      PermissionStorage.instance = new PermissionStorage();
    }
    return PermissionStorage.instance;
  }

  /**
   * 添加权限信息
   * @param routeName - 路由名称 (作为 key)
   * @param value - 权限值 (会自动去重)
   */
  add(routeName: string | symbol | null | undefined, value: string): void {
    if (!routeName) return;

    // 白名单中的权限值不存储
    if (whitePermissionValueList.includes(value)) {
      return;
    }

    const key = String(routeName);
    if (!this.storage.has(key)) {
      // 清空所有权限信息
      this.clearAll();
      this.storage.set(key, new Set());
    }
    this.storage.get(key)?.add(value);
  }

  /**
   * 通过路由名称获取权限值数组
   * @param routeName - 路由名称
   * @returns 权限值数组 (已去重)
   */
  get(routeName: string | symbol | null | undefined): string[] {
    if (!routeName) return [];
    const key = String(routeName);
    const valueSet = this.storage.get(key);
    return valueSet ? Array.from(valueSet) : [];
  }

  /**
   * 检查指定路由是否包含某个权限值
   * @param routeName - 路由名称
   * @param value - 权限值
   * @returns 是否存在
   */
  has(routeName: string | symbol | null | undefined, value: string): boolean {
    if (!routeName) return false;
    const key = String(routeName);
    return this.storage.get(key)?.has(value) ?? false;
  }

  /**
   * 获取所有路由名称
   * @returns 路由名称数组
   */
  getAllRouteNames(): string[] {
    return Array.from(this.storage.keys());
  }

  /**
   * 清空指定路由的权限信息
   * @param routeName - 路由名称
   */
  clear(routeName: string | symbol | null | undefined): void {
    if (!routeName) return;
    const key = String(routeName);
    this.storage.delete(key);
  }

  /**
   * 清空所有权限信息
   */
  clearAll(): void {
    this.storage.clear();
  }

  /**
   * 获取所有权限信息 (用于调试)
   * @returns 权限信息对象
   */
  getAll(): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    this.storage.forEach((valueSet, key) => {
      result[key] = Array.from(valueSet);
    });
    return result;
  }
}

export const permissionStorage = PermissionStorage.getInstance();
